/**
 * AR Aging & Collections — Phase 8.
 *
 * Pure selectors over GL state. AR is derived from the 1100 Accounts Receivable
 * subledger entries — no separate AR table, no risk of drift.
 *
 * Open balance per customer = sum(debits to 1100) - sum(credits to 1100).
 * Each open invoice = a debit-side JE line; payments reduce the balance FIFO.
 */
import type { JournalEntry } from "../glTypes";

const today = () => new Date().toISOString().slice(0, 10);

const daysBetween = (a: string, b: string) => {
  const ms = new Date(b).getTime() - new Date(a).getTime();
  return Math.max(0, Math.floor(ms / 86_400_000));
};

export type AgingBucket = "current" | "1-30" | "31-60" | "61-90" | "90+";

export const AGING_BUCKETS: AgingBucket[] = ["current", "1-30", "31-60", "61-90", "90+"];

export function bucketize(daysOpen: number): AgingBucket {
  if (daysOpen <= 0) return "current";
  if (daysOpen <= 30) return "1-30";
  if (daysOpen <= 60) return "31-60";
  if (daysOpen <= 90) return "61-90";
  return "90+";
}

export interface OpenInvoice {
  jeId: string;
  jeNumber: string;
  invoiceRef: string;        // line.memo (invoice id from posting rule)
  postingDate: string;
  jobId?: string;
  tradeType?: string;
  originalAmount: number;
  appliedAmount: number;
  openAmount: number;
  daysOpen: number;
  bucket: AgingBucket;
}

export interface CustomerAR {
  customerId: string;
  customerLabel: string;
  openInvoices: OpenInvoice[];
  totalOpen: number;
  oldestDaysOpen: number;
  bucketTotals: Record<AgingBucket, number>;
  lastActivityDate?: string;
  /** Credits (payments/credit memos) received in excess of billed debits.
   *  Always >= 0. A positive value means the customer has prepaid or
   *  overpaid and the surplus is not yet applied to a future invoice. */
  unappliedCredit: number;
}

/** Internal: collect every 1100 line keyed by customer. */
function collectArLines(journalEntries: JournalEntry[]) {
  const byCustomer = new Map<string, {
    debits: Array<{ entry: JournalEntry; amount: number; ref: string; jobId?: string; trade?: string }>;
    credits: Array<{ entry: JournalEntry; amount: number; ref: string }>;
  }>();
  for (const je of journalEntries) {
    if (je.status !== "posted") continue;
    for (const l of je.lines) {
      if (l.accountId !== "1100" || !l.customerId) continue;
      const slot = byCustomer.get(l.customerId) ?? { debits: [], credits: [] };
      if (l.side === "debit") {
        slot.debits.push({
          entry: je, amount: l.amount, ref: l.memo ?? je.number,
          jobId: l.jobId, trade: l.tradeType,
        });
      } else {
        slot.credits.push({ entry: je, amount: l.amount, ref: l.memo ?? je.number });
      }
      byCustomer.set(l.customerId, slot);
    }
  }
  return byCustomer;
}

/** FIFO-apply credits against oldest debits to compute per-invoice open balance. */
function ageCustomer(
  customerId: string,
  slot: ReturnType<typeof collectArLines> extends Map<string, infer V> ? V : never,
  asOf: string,
): CustomerAR {
  // Oldest first
  const debits = [...slot.debits].sort(
    (a, b) => a.entry.postingDate.localeCompare(b.entry.postingDate),
  );
  let creditPool = slot.credits.reduce((s, c) => s + c.amount, 0);

  const openInvoices: OpenInvoice[] = [];
  for (const d of debits) {
    const applied = Math.min(creditPool, d.amount);
    creditPool -= applied;
    const open = d.amount - applied;
    if (open <= 0.0001) continue;
    const days = daysBetween(d.entry.postingDate, asOf);
    openInvoices.push({
      jeId: d.entry.id,
      jeNumber: d.entry.number,
      invoiceRef: d.ref,
      postingDate: d.entry.postingDate,
      jobId: d.jobId,
      tradeType: d.trade,
      originalAmount: d.amount,
      appliedAmount: applied,
      openAmount: open,
      daysOpen: days,
      bucket: bucketize(days),
    });
  }

  const bucketTotals: Record<AgingBucket, number> =
    { current: 0, "1-30": 0, "31-60": 0, "61-90": 0, "90+": 0 };
  for (const inv of openInvoices) bucketTotals[inv.bucket] += inv.openAmount;

  const allActivity = [
    ...slot.debits.map(d => d.entry.postingDate),
    ...slot.credits.map(c => c.entry.postingDate),
  ].sort();
  const lastActivityDate = allActivity[allActivity.length - 1];

  const totalOpen = openInvoices.reduce((s, i) => s + i.openAmount, 0);
  const oldestDaysOpen = openInvoices.reduce((m, i) => Math.max(m, i.daysOpen), 0);

  // Whatever credit remains after FIFO-applying to every outstanding debit
  // is an overpayment / prepayment. Surface it explicitly instead of letting
  // it silently negate future open balances.
  const unappliedCredit = Math.max(0, creditPool);

  return {
    customerId,
    customerLabel: customerId
      .replace(/_/g, " ")
      .replace(/\b\w/g, c => c.toUpperCase()),
    openInvoices,
    totalOpen,
    oldestDaysOpen,
    bucketTotals,
    lastActivityDate,
    unappliedCredit,
  };
}

export function getCustomerARList(
  journalEntries: JournalEntry[],
  asOf: string = today(),
): CustomerAR[] {
  const m = collectArLines(journalEntries);
  const rows: CustomerAR[] = [];
  for (const [customerId, slot] of m) {
    const aged = ageCustomer(customerId, slot, asOf);
    // Surface anyone with either an open balance OR a credit on file —
    // unapplied credits are still a real obligation to the customer.
    if (aged.totalOpen > 0.005 || aged.unappliedCredit > 0.005) rows.push(aged);
  }
  return rows.sort((a, b) => b.totalOpen - a.totalOpen);
}

export function getCustomerAR(
  journalEntries: JournalEntry[],
  customerId: string,
  asOf: string = today(),
): CustomerAR | null {
  const m = collectArLines(journalEntries);
  const slot = m.get(customerId);
  if (!slot) return null;
  return ageCustomer(customerId, slot, asOf);
}

export interface ARAgingSummary {
  asOf: string;
  totalOpen: number;
  customerCount: number;
  bucketTotals: Record<AgingBucket, number>;
  bucketCustomerCount: Record<AgingBucket, number>;
  /** Open balance > 60d. The collections triage target. */
  overdueAmount: number;
  oldestDaysOpen: number;
  /** Sum of unapplied credits across all customers (prepayments / overpayments). */
  totalUnappliedCredit: number;
  unappliedCreditCustomerCount: number;
}

export function getARAgingSummary(
  journalEntries: JournalEntry[],
  asOf: string = today(),
): ARAgingSummary {
  const rows = getCustomerARList(journalEntries, asOf);
  const bucketTotals: Record<AgingBucket, number> =
    { current: 0, "1-30": 0, "31-60": 0, "61-90": 0, "90+": 0 };
  const bucketCustomerCount: Record<AgingBucket, number> =
    { current: 0, "1-30": 0, "31-60": 0, "61-90": 0, "90+": 0 };
  let oldest = 0;
  let totalUnappliedCredit = 0;
  let unappliedCreditCustomerCount = 0;
  for (const r of rows) {
    for (const b of AGING_BUCKETS) {
      bucketTotals[b] += r.bucketTotals[b];
      if (r.bucketTotals[b] > 0) bucketCustomerCount[b] += 1;
    }
    oldest = Math.max(oldest, r.oldestDaysOpen);
    if (r.unappliedCredit > 0.005) {
      totalUnappliedCredit += r.unappliedCredit;
      unappliedCreditCustomerCount += 1;
    }
  }
  const totalOpen = rows.reduce((s, r) => s + r.totalOpen, 0);
  const overdueAmount = bucketTotals["31-60"] + bucketTotals["61-90"] + bucketTotals["90+"];
  return {
    asOf,
    totalOpen,
    customerCount: rows.length,
    bucketTotals,
    bucketCustomerCount,
    overdueAmount,
    oldestDaysOpen: oldest,
    totalUnappliedCredit,
    unappliedCreditCustomerCount,
  };
}

// ─────────────────────────────────────────────────────────────
// Dunning ladder — what to do next per customer
// ─────────────────────────────────────────────────────────────

export type DunningStep = "none" | "reminder" | "first_notice" | "second_notice" | "final_notice" | "escalate";

export interface DunningRecommendation {
  step: DunningStep;
  label: string;
  rationale: string;
  tone: "neutral" | "warn" | "critical";
}

export function recommendDunning(c: CustomerAR): DunningRecommendation {
  if (c.totalOpen <= 0) return { step: "none", label: "None", rationale: "Paid in full.", tone: "neutral" };
  const d = c.oldestDaysOpen;
  if (d <= 7) return { step: "reminder", label: "Friendly reminder", rationale: `Oldest invoice ${d}d.`, tone: "neutral" };
  if (d <= 30) return { step: "first_notice", label: "First notice", rationale: `Oldest invoice ${d}d.`, tone: "neutral" };
  if (d <= 60) return { step: "second_notice", label: "Second notice", rationale: `${d}d overdue — escalate next cycle.`, tone: "warn" };
  if (d <= 90) return { step: "final_notice", label: "Final notice", rationale: `${d}d overdue — final demand before legal.`, tone: "warn" };
  return { step: "escalate", label: "Escalate to collections", rationale: `${d}d overdue.`, tone: "critical" };
}
